// Tiny client-side meta manager. We only run a single static index.html
// (Vite SPA), so per-route <title>, description, and canonical have to
// be set on mount. Open Graph tags are intentionally NOT mutated here —
// social-share crawlers don't run JS, so the static OG block in
// index.html is the source of truth for previews.

import { useEffect } from 'react';

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
}

export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDescription = readMeta('description');
    const prevCanonical = readLink('canonical');

    document.title = meta.title;
    setMeta('description', meta.description);
    setLink('canonical', meta.canonical);

    return () => {
      document.title = prevTitle;
      if (prevDescription !== null) setMeta('description', prevDescription);
      if (prevCanonical !== null) setLink('canonical', prevCanonical);
    };
  }, [meta.title, meta.description, meta.canonical]);
}

function readMeta(name: string): string | null {
  const tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  return tag?.getAttribute('content') ?? null;
}

function setMeta(name: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function readLink(rel: string): string | null {
  const tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  return tag?.getAttribute('href') ?? null;
}

function setLink(rel: string, href: string): void {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}
